/**
 * The mapper's `reason` string, taken apart for display.
 *
 * The backend composes it as `` `${basis}; ${adjustments.join(', ')}` `` (honey
 * `metric-mapper.service.ts`), so a real candidate reads
 * `token similarity 0.62; unit contradicts (litres vs km)`. Rendered as one line
 * that buries the decisive part — a unit disagreement — at the end, in the same
 * grey as everything else. Splitting it lets the caller give each part the weight
 * it deserves, and lets the score-restating bases be replaced with words.
 */
export interface ParsedMappingReason {
  /** Why this candidate was matched at all, in reviewer-facing words. */
  basis: string;
  /**
   * The unit disagreement the mapper found, phrased for a reviewer, or null when
   * the units agree or the mapper had no unit to compare.
   */
  unitConflict: string | null;
  /** The remaining context adjustments, in reviewer-facing words. */
  adjustments: Array<string>;
}

/** Separates the matching basis from the mapper's context adjustments. */
const BASIS_SEPARATOR = '; ';

/** Separates one context adjustment from the next. No adjustment contains a comma. */
const ADJUSTMENT_SEPARATOR = ', ';

/** Marks the mapper's duplicate-name branch, whose wording the layout replaces. */
const SHARED_NAME_MARKER = ', but name is shared across metricGroups';

/** `unit contradicts (<catalogue unit> vs <unit in the file>)`. */
const UNIT_CONFLICT_PATTERN = /^unit contradicts \((.+) vs (.+)\)$/;

/**
 * Bases matched by exact text. The mapper's own strings restate the tier; these
 * say what the tier MEANS to someone deciding whether the metric is right.
 */
const EXACT_BASIS_WORDS: Record<string, string> = {
  'exact name match': 'The catalogue name matches the label exactly.',
  'normalised name match': 'The names match once case and punctuation are ignored.'
};

/**
 * Bases matched by prefix, because the mapper appends the score to them — which is
 * the duplication this parser exists to remove (the score is already a chip).
 */
const PREFIXED_BASIS_WORDS: Array<{ prefix: string; words: string }> = [
  { prefix: 'token similarity', words: 'The wording overlaps.' },
  { prefix: 'embedding similarity', words: 'Close in meaning rather than in wording.' },
  {
    prefix: 'exact match after stripping',
    words: 'The names match once the leading "Category:" is dropped.'
  }
];

/** Context adjustments matched by exact text. */
const ADJUSTMENT_WORDS: Record<string, string> = {
  'label prefix names this group': 'The label itself names this group.',
  'unit family agrees': 'The unit agrees with what this metric expects.'
};

/**
 * Rewrites the matching basis into reviewer-facing words.
 *
 * An unrecognised basis is returned VERBATIM rather than dropped: the mapper is
 * free to add tiers, and swallowing one would hide exactly the explanation the
 * reviewer opened the picker to read.
 */
function toBasisWords(basis: string): string {
  const trimmed = basis.trim();
  if (EXACT_BASIS_WORDS[trimmed] !== undefined) {
    return EXACT_BASIS_WORDS[trimmed];
  }
  const prefixed = PREFIXED_BASIS_WORDS.find((entry) => trimmed.startsWith(entry.prefix));
  return prefixed !== undefined ? prefixed.words : trimmed;
}

/** Rewrites one context adjustment into reviewer-facing words, verbatim if unknown. */
function toAdjustmentWords(adjustment: string): string {
  const trimmed = adjustment.trim();
  return ADJUSTMENT_WORDS[trimmed] ?? trimmed;
}

/**
 * Phrase a unit disagreement from the mapper's side of it. The mapper writes the
 * CATALOGUE unit first and the file's second, so the sentence has to keep that
 * order to stay true.
 */
function toUnitConflictWords(adjustment: string): string | null {
  const match = UNIT_CONFLICT_PATTERN.exec(adjustment.trim());
  if (match === null) {
    return null;
  }
  const [, expectedUnit, unitInFile] = match;
  return `Expects ${expectedUnit} — the file gave ${unitInFile}.`;
}

/**
 * Split a mapping candidate's `reason` into its basis, its unit conflict and its
 * remaining adjustments, each phrased for a reviewer.
 * @param {string} reason The raw `reason` from a `MappingCandidate`.
 * @returns {ParsedMappingReason}
 */
export function parseMappingReason(reason: string): ParsedMappingReason {
  const [rawBasis, ...rest] = reason.split(BASIS_SEPARATOR);
  // The duplicate-name branch spells out that the name is shared; the picker shows
  // that structurally instead, so only the tier part of the sentence is kept.
  const [basisText] = rawBasis.split(SHARED_NAME_MARKER);

  const rawAdjustments = rest
    .join(BASIS_SEPARATOR)
    .split(ADJUSTMENT_SEPARATOR)
    .map((adjustment) => adjustment.trim())
    .filter((adjustment) => adjustment !== '');

  let unitConflict: string | null = null;
  const adjustments: Array<string> = [];
  for (const adjustment of rawAdjustments) {
    const conflict = toUnitConflictWords(adjustment);
    if (conflict === null) {
      adjustments.push(toAdjustmentWords(adjustment));
    } else {
      unitConflict = conflict;
    }
  }

  return { basis: toBasisWords(basisText), unitConflict, adjustments };
}
